// Step metadata — visual/category enrichment for the simple step strings
// stored in /src/data/routines.js. Index-based mapping.

import {
  Droplets,
  Sparkles,
  Sun,
  Moon,
} from "lucide-react";

// Skincare AM — 5 standard steps
export const SKIN_AM_META = [
  { category: "Cleansing", type: "Cleanser", emoji: "🧴", bg: "#FDE8F0", instruction: "Massage gently in circular motions for 60 seconds, then rinse with lukewarm water." },
  { category: "Toning", type: "Toner", emoji: "💧", bg: "#E8F0FD", instruction: "Apply with a cotton pad or pat directly onto skin. Focus on areas of concern.", timeLabel: "30 sec" },
  { category: "Treatment", type: "Serum", emoji: "✨", bg: "#FDF5E8", instruction: "Apply 2–3 drops and press into skin. Targets pores and dark spots." },
  { category: "Moisturizing", type: "Moisturizer", emoji: "🍃", bg: "#E8FDF0", instruction: "Smooth a pea-size amount over face and neck in upward strokes." },
  { category: "Protection", type: "SPF", emoji: "☀️", bg: "#FFFBE8", instruction: "Apply two finger-lengths to face, neck and ears. Reapply every 2 hours.", timeLabel: "SPF 50" },
];

// Skincare PM — typically 4–5 steps
export const SKIN_PM_META = [
  { category: "Cleansing", type: "Pre-cleanse", emoji: "🧴", bg: "#FDE8F0", instruction: "Massage onto dry skin for 60 seconds to dissolve sunscreen and sebum, then emulsify." },
  { category: "Cleansing", type: "Cleanser", emoji: "🫧", bg: "#FDE8F0", instruction: "Second cleanse — gentle gel or cream cleanser, 60 seconds, rinse." },
  { category: "Treatment", type: "Treatment", emoji: "✨", bg: "#FDF5E8", instruction: "Apply your active (retinol / BHA / niacinamide) — start slow, build tolerance." },
  { category: "Hydration", type: "Essence / Serum", emoji: "💧", bg: "#E8F0FD", instruction: "Press a hydrating layer into damp skin to lock moisture in." },
  { category: "Moisturizing", type: "Night Cream", emoji: "🌙", bg: "#E8FDF0", instruction: "Seal everything with a rich barrier-supporting cream." },
];

// Hair — generic steps
export const HAIR_META = [
  { category: "Hydration", type: "Spritz", emoji: "💦", bg: "#E8F0FD", instruction: "Spritz hair with water or leave-in spray until damp." },
  { category: "Detangle", type: "Detangler", emoji: "🌿", bg: "#E8FDF0", instruction: "Finger-detangle in sections on damp hair with conditioner for slip." },
  { category: "Moisture", type: "Leave-in", emoji: "🧴", bg: "#F0E8FD", instruction: "Apply leave-in conditioner generously from mid-length to ends." },
  { category: "Seal", type: "Oil", emoji: "🪔", bg: "#FDF5E8", instruction: "Seal in moisture with a light oil (jojoba, argan or sweet almond)." },
  { category: "Style", type: "Cream / Comb", emoji: "💆🏾‍♀️", bg: "#FDE8F0", instruction: "Define, style or protect — twist-out, bun, or satin bonnet for bed." },
];

// Concern → step category mapping for the "✓ Match" badge
const CONCERN_TO_CATEGORY = {
  "Acne": ["Cleansing", "Treatment"],
  "Oiliness": ["Cleansing", "Toning"],
  "Hyperpigmentation": ["Treatment", "Protection"],
  "Dark spots": ["Treatment", "Protection"],
  "Uneven tone": ["Treatment", "Protection"],
  "Dryness": ["Hydration", "Moisturizing", "Moisture"],
  "Sensitivity": ["Moisturizing"],
  "Redness": ["Moisturizing", "Hydration"],
  "Fine lines": ["Treatment", "Moisturizing"],
  "Breakage": ["Moisture", "Seal"],
  "Frizz": ["Hydration", "Seal"],
  "Low growth": ["Hydration", "Seal"],
  "Scalp issues": ["Detangle", "Hydration"],
  "Thinning": ["Hydration", "Seal"],
  "Heat damage": ["Moisture", "Seal"],
  "Lack of definition": ["Style"],
};

// Enrich a routine step array with metadata.
// stepStrings — the simple strings from routines.js
// timeOfDay — "AM" | "PM"
// kind — "skin" | "hair"
// userConcerns — array of concern strings from profile.concerns
export function enrichSteps(stepStrings, timeOfDay, kind, userConcerns = []) {
  const metaSet = kind === "skin" ? (timeOfDay === "AM" ? SKIN_AM_META : SKIN_PM_META) : HAIR_META;
  return (stepStrings || []).map((name, i) => {
    const meta = metaSet[Math.min(i, metaSet.length - 1)];
    const isMatch = userConcerns.some(
      (c) => (CONCERN_TO_CATEGORY[c] || []).includes(meta.category)
    );
    return {
      ...meta,
      name,
      index: i,
      isMatch,
    };
  });
}

export const TIME_ICONS = { AM: Sun, PM: Moon };
export const RAINBOW = { Droplets, Sparkles };
